import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ApiError } from '@/api/client'
import { useAuth } from '@/auth/useAuth'
import { completeMicrosoftRedirect, isOidcEnabled } from '@/auth/oidc'

/**
 * Handles the Entra redirect return at `/auth/callback`, exchanges the ID
 * token for an ARIES session, then sends the user into the app.
 */
export function AuthCallbackPage() {
  const { isAuthenticated, completeOidcSession } = useAuth()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOidcEnabled()) {
      setError('Microsoft sign-in is not enabled in this build.')
      return
    }

    let cancelled = false

    async function finish() {
      try {
        const result = await completeMicrosoftRedirect()
        if (cancelled) return
        if (!result?.idToken) {
          setError('Microsoft sign-in did not return a token. Try again.')
          return
        }
        await completeOidcSession(result.idToken)
        if (!cancelled) navigate('/', { replace: true })
      } catch (caught) {
        if (cancelled) return
        setError(
          caught instanceof ApiError
            ? caught.message
            : caught instanceof Error
              ? caught.message
              : 'Microsoft sign-in failed.',
        )
      }
    }

    void finish()
    return () => {
      cancelled = true
    }
  }, [completeOidcSession, navigate])

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  return (
    <div className="sign-in sign-in--split">
      <div className="sign-in__panel sign-in__panel--brand" aria-hidden="true">
        <div className="sign-in__brand-mark">ARIES</div>
      </div>
      <div className="sign-in__panel sign-in__panel--form">
        <div className="sign-in__form-wrap">
          {error ? (
            <>
              <h1 className="sign-in__title">Sign-in incomplete</h1>
              <div className="alert alert--error" role="alert">
                {error}
              </div>
              <a className="button button--primary" href="/sign-in">
                Back to sign in
              </a>
            </>
          ) : (
            <>
              <h1 className="sign-in__title">Completing Microsoft sign-in…</h1>
              <p className="sign-in__lede">Verifying your Veersa Tech account with ARIES.</p>
              <div className="loading" role="status" aria-live="polite">
                Loading…
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
